/**
 * result-v2/ui/primitives.tsx
 * ---------------------------------------------------------------------------
 * The whole layout vocabulary for the redesigned result views.
 *
 * Layout rule that matters: columns are ASSIGNED, never balanced by the
 * browser. `<Cols>` + `<Stack>` let the template decide what sits in which
 * column, which is what removes the empty-column problem you get from
 * CSS multi-column / masonry when a result has only three or four cards.
 */
import * as React from 'react'
import { Box, CircularProgress, Dialog, IconButton, Typography, useTheme } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { radii, sourceColor, initials, tokensFor, Tokens } from '../tokens'
import { useLinkPreview } from './linkPreview'
import { IconTile } from '../../../component/source-icon/SourceIcon'
import { categoryHue, useSourceIcons } from '../../../component/source-icon/sourceIconStore'
import noResultsLight from '../../../asset/result-v2/no-results-light.png'
import noResultsDark from '../../../asset/result-v2/no-results-dark.png'


/**
 * Capitalise the first letter of enum-ish values ("low", "daytime", "rural")
 * so every card reads consistently. Deliberately conservative: values holding
 * an @, a URL, a leading digit or an existing capital are returned untouched,
 * so e-mails, handles, scores and proper nouns are never mangled.
 */
export const sentence = (v: React.ReactNode): React.ReactNode => {
  if (typeof v !== 'string') return v
  const s = v.trim()
  if (!s) return v
  if (s.includes('@') || /^https?:/i.test(s) || /^[^a-z]/.test(s)) return v
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export const useTokens = (): Tokens => {
  const theme = useTheme()
  return tokensFor(theme.palette.mode)
}

/**
 * True for anything the browser can already fetch/read as-is. Deliberately
 * does NOT treat a bare leading "/" as a site-relative path — base64 JPEG
 * data very commonly starts with "/9j/" (the base64 encoding of JPEG's
 * 0xFFD8FF magic bytes), which was being misread as an absolute URL and
 * left unprefixed. Provider avatar values are never actually site-relative
 * paths, so this is safe to drop.
 */
function isUrlLike(s: string): boolean {
  return s.startsWith('data:') || s.startsWith('//') || /^https?:\/\//i.test(s)
}

/**
 * Some providers (imo, telegram and others funnelled through the generic
 * ['avatar','image','photoUrl','photo'] collector in
 * store/searcher/service.ts) hand back raw base64 image data with no
 * `data:` URI prefix, sometimes with embedded whitespace/newlines, a
 * missing trailing `=` padding, or the URL-safe alphabet (`-`/`_` instead
 * of `+`/`/`). All of those make an otherwise-valid base64 payload fail to
 * decode as-is, which is why fixing just the missing prefix wasn't enough.
 * This strips whitespace, swaps the URL-safe alphabet back, and re-pads
 * before handing it back — returns null if what's left doesn't look like
 * base64 at all (too short, or has characters outside the base64 set).
 */
function normalizeBase64(s: string): string | null {
  const stripped = s.replace(/\s+/g, '')
  if (stripped.length < 40) return null // too short to plausibly be a photo
  if (!/^[A-Za-z0-9+/_-]+={0,2}$/.test(stripped)) return null
  const standardAlphabet = stripped.replace(/-/g, '+').replace(/_/g, '/')
  const paddingNeeded = (4 - (standardAlphabet.length % 4)) % 4
  return standardAlphabet + '='.repeat(paddingNeeded)
}

/**
 * The `avatar`/`image`/`photo` field on a provider entity is either a real
 * link or raw base64 image data (see normalizeBase64 above) — never
 * anything else worth trying to render. Junk values some providers send in
 * place of a real one ("null", "undefined", empty) are treated as absent.
 */
export function toImageSrc(value: unknown): string {
  const s = typeof value === 'string' ? value.trim() : ''
  if (!s || /^(null|undefined)$/i.test(s)) return ''
  if (isUrlLike(s)) return s
  const b64 = normalizeBase64(s)
  return b64 ? `data:image/jpeg;base64,${b64}` : ''
}

/** A provider photo/avatar — see toImageSrc for the link-vs-base64 check. */
export function Photo({
  value,
  alt = '',
  sx,
  onClick,
  onLoadError,
}: {
  value: unknown
  alt?: string
  sx?: object
  /** Pass usePhotoLightbox's openAt(i) to make the tile click-to-enlarge. */
  onClick?: () => void
  /**
   * Fires once when the src toImageSrc produced turns out not to actually
   * load (dead link, corrupt data — can't be known upfront, only once the
   * browser tries). Grids that pre-filter with toImageSrc still end up
   * with the placeholder icon for these; wire this up to drop the entry
   * from the caller's list so the tile disappears instead of sitting
   * empty. Does NOT fire just because toImageSrc(value) was empty to begin
   * with — filter those out before rendering instead, no need to wait on
   * a failed request for something that was never going to load.
   */
  onLoadError?: () => void
}) {
  const src = toImageSrc(value)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => setFailed(false), [src])

  if (!src || failed) return null

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onClick={onClick}
      sx={{ width: '100%', height: '100%', objectFit: 'cover', cursor: onClick ? 'pointer' : undefined, ...sx }}
      onError={() => {
        setFailed(true)
        onLoadError?.()
      }}
    />
  )
}

/**
 * Click-to-enlarge for a gallery of Photo tiles: pass every photo's raw
 * value (same shape Photo takes) in display order, get back `openAt(i)` to
 * wire onto each tile's onClick, and `lightbox` to render once per
 * gallery. The dialog carousels through the same list — prev/next
 * buttons, left/right arrow keys, Escape to close.
 */
export function usePhotoLightbox(values: unknown[]) {
  const [index, setIndex] = React.useState<number | null>(null)
  const openAt = (i: number) => setIndex(i)
  const close = () => setIndex(null)
  const prev = React.useCallback(
    () => setIndex((i) => (i === null || values.length === 0 ? i : (i - 1 + values.length) % values.length)),
    [values.length],
  )
  const next = React.useCallback(
    () => setIndex((i) => (i === null || values.length === 0 ? i : (i + 1) % values.length)),
    [values.length],
  )

  React.useEffect(() => {
    if (index === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, prev, next])

  const lightbox =
    index !== null ? (
      <PhotoLightbox values={values} index={index} onClose={close} onPrev={prev} onNext={next} />
    ) : null

  return { openAt, lightbox }
}

/**
 * Lightbox content for one item. Tries it as an image first (that's what
 * every other caller of usePhotoLightbox actually is); if the browser fails
 * to load it as an image, it's a story video that got fed into a photo
 * gallery (Stories carousel shares its lightbox with everything else) — at
 * that point it falls back to a native <video controls> instead of leaving
 * the dialog blank. Resets per index so navigating to the next item doesn't
 * carry over the previous one's video fallback.
 */
function LightboxMedia({ src, tk }: { src: string; tk: Tokens }) {
  const [isVideo, setIsVideo] = React.useState(false)
  React.useEffect(() => setIsVideo(false), [src])

  if (!src) return <Box sx={{ color: tk.text3, fontSize: 13 }}>Media unavailable</Box>

  return isVideo ? (
    <Box
      component="video"
      src={src}
      controls
      autoPlay
      playsInline
      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
    />
  ) : (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setIsVideo(true)}
      sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
    />
  )
}

function PhotoLightbox({
  values,
  index,
  onClose,
  onPrev,
  onNext,
}: {
  values: unknown[]
  index: number
  onClose: () => void
  onPrev: () => void
  onNext: () => void
}) {
  const tk = useTokens()
  const src = toImageSrc(values[index])
  const hasMultiple = values.length > 1

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      // Explicit zIndex: this lightbox can itself be opened from inside
      // another Dialog (e.g. the IRBI assistant's "Details" dialog, which
      // sits at 1500 to clear its own Drawer host — see AskIrbiAssistant.tsx).
      // MUI Dialogs default to theme.zIndex.modal (1300), so without this the
      // lightbox would render behind whatever Dialog opened it.
      sx={{ zIndex: 1600 }}
      PaperProps={{ sx: { background: 'transparent', boxShadow: 'none', overflow: 'visible', m: '16px' } }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 'min(82vw, 640px)',
          height: 'min(82vh, 640px)',
          background: tk.surface,
          border: `1px solid ${tk.border}`,
          borderRadius: `${radii.card}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <LightboxMedia src={src} tk={tk} />

        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{ position: 'absolute', top: 8, right: 8, background: tk.surface2, '&:hover': { background: tk.surface3 } }}
        >
          <CloseIcon sx={{ fontSize: 18, color: tk.text }} />
        </IconButton>

        {hasMultiple && (
          <>
            <IconButton
              onClick={onPrev}
              aria-label="Previous photo"
              sx={{
                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                background: tk.surface2, '&:hover': { background: tk.surface3 },
              }}
            >
              <ChevronLeftIcon sx={{ color: tk.text }} />
            </IconButton>
            <IconButton
              onClick={onNext}
              aria-label="Next photo"
              sx={{
                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                background: tk.surface2, '&:hover': { background: tk.surface3 },
              }}
            >
              <ChevronRightIcon sx={{ color: tk.text }} />
            </IconButton>
            <Box
              sx={{
                position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)',
                background: tk.surface2, color: tk.text2, fontSize: 12, fontWeight: 600,
                px: '10px', py: '4px', borderRadius: '999px',
              }}
            >
              {index + 1} / {values.length}
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  )
}

/* ------------------------------------------------------------------ */
/* Layout                                                              */
/* ------------------------------------------------------------------ */

/**
 * Explicit column container. `n` columns on desktop, collapses responsively.
 *
 * Two ways to use it:
 *  - Two deliberately-paired columns (e.g. GeoTemplate's map vs. network
 *    details): wrap each side in <Stack>, keep the default `align="stretch"`
 *    so both columns share the row's height.
 *  - A flat run of same-weight cards (e.g. PersonTemplate's Photos / Names /
 *    Contact / ... cards): pass the cards directly as children with
 *    `align="start"`. Grid's default auto-flow already places them
 *    left-to-right, wrapping to the next row after `n` — that self-balances
 *    for any data volume without one column ever running far taller than
 *    its neighbours, which is what caused the big empty gaps before.
 */
export function Cols({ n = 2, align = 'stretch', children }: { n?: 2 | 3; align?: 'start' | 'stretch'; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: '14px',
        alignItems: align,
        gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`,
        ...(n === 3 ? { '@media (max-width:1080px)': { gridTemplateColumns: 'repeat(2, minmax(0,1fr))' } } : {}),
        '@media (max-width:780px)': { gridTemplateColumns: '1fr' },
      }}
    >
      {children}
    </Box>
  )
}

/**
 * A single column: cards stacked vertically, filling the full height Cols
 * gives it. The last rendered card (React already drops falsy `{cond && …}`
 * children, so this is the last *visible* card, not just the last JSX
 * expression) grows to soak up whatever height its column is short by — its
 * border/background extends down to match the tallest column, while its own
 * content stays normal block flow, i.e. pinned to the top rather than
 * re-centered or stretched apart.
 */
export function Stack({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        minWidth: 0,
        height: '100%',
        '& > *:last-child': { flex: '1 0 auto' },
      }}
    >
      {children}
    </Box>
  )
}

/** Two equal halves inside one card — used to stop tall thin boxes. */
export function Duo({ children, gap = 18 }: { children: React.ReactNode; gap?: number }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
        gap: `${gap}px`,
        '@media (max-width:860px)': { gridTemplateColumns: '1fr' },
      }}
    >
      {children}
    </Box>
  )
}

/** Three hairline-divided sections inside one wide card. */
export function Tri({ children }: { children: React.ReactNode }) {
  const tk = useTokens()
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0,1fr))',
        '& > div': { px: '16px' },
        '& > div:first-of-type': { pl: 0 },
        '& > div:last-of-type': { pr: 0 },
        '& > div + div': { borderLeft: `1px solid ${tk.border}` },
        '@media (max-width:860px)': {
          gridTemplateColumns: '1fr',
          gap: '14px',
          '& > div': { px: 0 },
          '& > div + div': { borderLeft: 0, borderTop: `1px solid ${tk.border}`, pt: '14px' },
        },
      }}
    >
      {children}
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

type CardTone = 'default' | 'accent' | 'good' | 'bad'

export function ResCard({
  title,
  count,
  action,
  tone = 'default',
  fill,
  children,
}: {
  title?: string
  count?: number
  action?: React.ReactNode
  tone?: CardTone
  /** stretch the card to the height of its grid cell */
  fill?: boolean
  children: React.ReactNode
}) {
  const tk = useTokens()
  const toneSx =
    tone === 'accent'
      ? { borderColor: 'rgba(245,158,11,.45)', background: 'linear-gradient(180deg,rgba(245,158,11,.06),transparent 55%)' }
      : tone === 'good'
      ? { borderColor: 'rgba(16,185,129,.4)', background: 'linear-gradient(180deg,rgba(16,185,129,.06),transparent 55%)' }
      : tone === 'bad'
      ? { borderColor: 'rgba(239,68,68,.4)', background: 'linear-gradient(180deg,rgba(239,68,68,.06),transparent 55%)' }
      : { background: tk.surface }

  return (
    <Box
      sx={{
        border: `1px solid ${tk.border}`,
        borderRadius: `${radii.card}px`,
        p: '14px 15px',
        minWidth: 0,
        ...(fill ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
        ...toneSx,
      }}
    >
      {(title || action) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '11px', flexWrap: 'wrap' }}>
          {title && (
            <Typography
              component="h3"
              sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: tk.text3, fontWeight: 700 }}
            >
              {title}
            </Typography>
          )}
          {count !== undefined && (
            <Box component="span" sx={{ background: tk.surface3, color: tk.text2, borderRadius: '20px', px: '7px', fontSize: 10, fontWeight: 700 }}>
              {count}
            </Box>
          )}
          {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
        </Box>
      )}
      {fill ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            pr: '4px',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '3px' },
            '&:hover::-webkit-scrollbar-thumb': { background: tk.borderStrong },
          }}
        >
          {children}
        </Box>
      ) : (
        children
      )}
    </Box>
  )
}

/** Small uppercase heading for a section inside a card. */
export function Sech({ children, count }: { children: React.ReactNode; count?: number }) {
  const tk = useTokens()
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', mb: '9px' }}>
      <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.08em', color: tk.text3, fontWeight: 700 }}>
        {children}
      </Typography>
      {count !== undefined && (
        <Box component="span" sx={{ background: tk.surface3, color: tk.text2, borderRadius: '20px', px: '6px', fontSize: 9.5, fontWeight: 700 }}>
          {count}
        </Box>
      )}
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Key facts strip                                                     */
/* ------------------------------------------------------------------ */

export type Stat = { label: string; value: React.ReactNode; sub?: string; tone?: 'hl' | 'ok' | 'warn' | 'bad' }

export function Stats({ items }: { items: Stat[] }) {
  const tk = useTokens()
  const colour = (t?: Stat['tone']) =>
    t === 'hl' ? '#2D89F4' : t === 'ok' ? '#10B981' : t === 'warn' ? '#F59E0B' : t === 'bad' ? '#EF4444' : tk.text
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))', gap: '9px' }}>
      {items.map((s, i) => (
        <Box key={i} sx={{ background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, p: '10px 11px', minHeight: 66, display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.06em', color: tk.text3, fontWeight: 700 }}>
            {s.label}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mt: '2px', color: colour(s.tone) }}>{sentence(s.value)}</Typography>
          <Typography sx={{ fontSize: 10, color: tk.text3, mt: 'auto', pt: '2px', minHeight: 13 }}>{s.sub ? sentence(s.sub) : '\u00A0'}</Typography>
        </Box>
      ))}
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/* Value rows — one row per VALUE, sources as badges                   */
/* ------------------------------------------------------------------ */

export type FlagTone = 'primary' | 'conflict' | 'bad' | 'ok' | 'variant'
export type ValueRow = {
  value: React.ReactNode
  /** provider keys, e.g. ['tc','getcontact'] */
  sources?: string[]
  note?: string
  flag?: { tone: FlagTone; label: string }
}

/*
 * A source's icon from the admin's source icon set (System → Search → Source
 * icons). A name the set does not know — a network named in a provider's
 * answer, a subreddit — keeps the coloured initials; `plain` asks for them
 * outright. The theme comes from MUI's palette mode, as the rest of result-v2.
 */
export function SourceBadge({ source, plain }: { source: string; plain?: boolean }) {
  const theme = useTheme()
  const set = useSourceIcons()
  const spec = plain ? undefined : set?.icons[source]
  if (spec) {
    return (
      <IconTile spec={spec} hue={categoryHue(set, spec.category)} look={set?.style} size={18} title={spec.label || source}
        mode={theme.palette.mode === 'dark' ? 'dark' : 'light'} />
    )
  }
  return (
    <Box
      title={source}
      sx={{
        width: 18, height: 18, borderRadius: '5px', display: 'grid', placeItems: 'center',
        fontSize: 8, fontWeight: 800, color: '#fff', flex: 'none', background: sourceColor(source),
      }}
    >
      {initials(source)}
    </Box>
  )
}

export function Flag({ tone, label }: { tone: FlagTone; label: string }) {
  const map: Record<FlagTone, [string, string]> = {
    primary: ['#1559B0', 'rgba(45,137,244,.12)'],
    conflict: ['#F59E0B', 'rgba(245,158,11,.14)'],
    bad: ['#EF4444', 'rgba(239,68,68,.12)'],
    ok: ['#10B981', 'rgba(16,185,129,.12)'],
    variant: ['#7C5CFF', 'rgba(124,92,255,.12)'],
  }
  const [c, bg] = map[tone]
  return (
    <Box component="span" sx={{ fontSize: 9.5, fontWeight: 800, px: '6px', py: '1px', borderRadius: '5px', color: c, background: bg, whiteSpace: 'nowrap' }}>
      {label}
    </Box>
  )
}

/**
 * Shared "+N more / show less" toggle. Every panel that can grow past a
 * handful of rows (ValueList, Chips, the inline breach list) caps itself at
 * a fixed row count instead of stretching the card to whatever the longest
 * sibling column happens to be — that's what was leaving the big empty
 * areas inside short cards. One flat, predictable card height per panel,
 * with the rest a click away, keeps the 3-column grid close to even for
 * ANY data volume without reshuffling which card goes in which column.
 */
export function useShowMore<T>(items: T[], cap: number) {
  const [open, setOpen] = React.useState(false)
  const visible = open ? items : items.slice(0, cap)
  return { visible, open, setOpen, hiddenCount: Math.max(0, items.length - cap) }
}

export function ShowMoreButton({ open, hiddenCount, onClick }: { open: boolean; hiddenCount: number; onClick: () => void }) {
  if (hiddenCount <= 0 && !open) return null
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{ mt: '9px', border: 0, background: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#2D89F4', p: 0 }}
    >
      {open ? 'show less' : `+${hiddenCount} more`}
    </Box>
  )
}

export function ValueList({ rows, cap }: { rows: ValueRow[]; cap?: number }) {
  const tk = useTokens()
  const limit = cap ?? 5
  const { visible, open, setOpen, hiddenCount } = useShowMore(rows, limit)

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {visible.map((r, i) => (
          <Box key={i} sx={{ display: 'flex', gap: '9px', alignItems: 'flex-start', fontSize: 13 }}>
            <Box sx={{ display: 'flex', gap: '3px', flex: 'none', pt: '2px' }}>
              {(r.sources || []).map((s) => (
                <SourceBadge key={s} source={s} />
              ))}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography component="span" sx={{ fontWeight: 600, wordBreak: 'break-word', lineHeight: 1.35, display: 'block', color: tk.text }}>
                {r.value}
              </Typography>
              {(r.flag || r.note) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', mt: '3px', flexWrap: 'wrap' }}>
                  {r.flag && <Flag {...r.flag} />}
                  {r.note && <Typography component="span" sx={{ fontSize: 10.5, color: tk.text3 }}>{r.note}</Typography>}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Field table, chips, warnings, verdict, raw                          */
/* ------------------------------------------------------------------ */

const IMAGE_FIELD_KEY_RE = /image|avatar|photo|picture|thumbnail|\bpic\b/i

/**
 * Raw provider dumps (ProviderCoverage's expanded field table) show every
 * field as text, including ones that are actually a photo — a truecallerstatic
 * URL under "Image", a base64 blob with no prefix under "Avatar". Rows whose
 * key name says it's an image, or whose value is unambiguously base64 image
 * data (regardless of key name — that's never a coincidence), render as a
 * thumbnail instead of the raw string. Everything else — including normal
 * http(s) links that aren't image fields — still renders as plain text, so
 * a "Profile URL" field doesn't turn into a broken image icon.
 */
export function FieldTable({ rows }: { rows: [string, React.ReactNode][] }) {
  const tk = useTokens()

  // Decorate each row once so the lightbox gallery only ever contains rows
  // actually rendered as a photo — otherwise prev/next in the dialog would
  // wander into unrelated text fields sitting between two image fields.
  const decorated = rows.map(([k, v]) => {
    const raw = typeof v === 'string' ? v.trim() : ''
    const candidate = raw ? toImageSrc(raw) : ''
    const showAsPhoto = !!candidate && (IMAGE_FIELD_KEY_RE.test(k) || candidate.startsWith('data:'))
    return { k, v, raw, showAsPhoto }
  })
  const photoValues = decorated.filter((d) => d.showAsPhoto).map((d) => d.raw)
  const { openAt, lightbox } = usePhotoLightbox(photoValues)
  let photoIndex = -1

  return (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '7px 14px', fontSize: 12.5, m: 0, alignItems: 'center' }}>
      {decorated.map((d, i) => {
        if (d.showAsPhoto) photoIndex += 1
        const thisPhotoIndex = photoIndex

        return (
          <React.Fragment key={i}>
            <Box component="dt" sx={{ color: tk.text3 }}>{d.k}</Box>
            <Box component="dd" sx={{ m: 0, fontWeight: 600, wordBreak: 'break-word', color: tk.text }}>
              {d.showAsPhoto ? (
                <Box sx={{ width: 64, height: 64, borderRadius: `${radii.md}px`, overflow: 'hidden', background: tk.surface3 }}>
                  <Photo value={d.raw} onClick={() => openAt(thisPhotoIndex)} />
                </Box>
              ) : (
                d.v
              )}
            </Box>
          </React.Fragment>
        )
      })}
      {lightbox}
    </Box>
  )
}

export function Chips({ items, tone, cap }: { items: React.ReactNode[]; tone?: 'b' | 'w' | 'd' | 'g'; cap?: number }) {
  const tk = useTokens()
  const map: Record<string, [string, string]> = {
    b: ['#1559B0', 'rgba(45,137,244,.12)'],
    w: ['#F59E0B', 'rgba(245,158,11,.14)'],
    d: ['#EF4444', 'rgba(239,68,68,.12)'],
    g: ['#10B981', 'rgba(16,185,129,.12)'],
  }
  const [c, bg] = tone ? map[tone] : [tk.text2, tk.surface3]
  const { visible, open, setOpen, hiddenCount } = useShowMore(items, cap ?? items.length)
  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {visible.map((x, i) => (
          <Box key={i} component="span" sx={{ fontSize: 11.5, fontWeight: 600, px: '10px', py: '4px', borderRadius: '8px', background: bg, color: c }}>
            {sentence(x)}
          </Box>
        ))}
      </Box>
      {cap != null && <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />}
    </>
  )
}

/**
 * Round breach-provider avatar for `LogoPath` (HIBP's field name for its
 * breach logos). Renders nothing — not a placeholder, just nothing — when
 * there's no URL or the image fails to load, so a missing/dead logo never
 * leaves a broken-image icon or an empty grey circle in the list.
 */
function BreachLogo({ src }: { src?: string }) {
  const tk = useTokens()
  const [broken, setBroken] = React.useState(false)
  if (!src || broken) return null
  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setBroken(true)}
      sx={{
        width: 26, height: 26, borderRadius: '50%', objectFit: 'cover', flex: 'none',
        border: `1px solid ${tk.border}`, background: tk.surface2,
      }}
    />
  )
}

/**
 * HIBP's `Description` is a snippet of HTML — usually a sentence with one or
 * two `<a href>` links to write-ups/coverage of the breach, occasionally a
 * bare URL instead. Ported from the legacy popup's own `HbpCommon.tsx`
 * (`renderDescription`), which already solved parsing this without pulling
 * in a full HTML renderer, so those links stay clickable instead of being
 * stripped out along with the markup.
 */
function renderBreachDescription(desc: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = []
  const anchorRe = /<a\s+[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi
  const stripTags = (s: string) => s.replace(/<[^>]+>/g, '')
  const linkSx = { color: '#2D89F4', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

  const linkify = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = []
    const urlRe = /((https?:\/\/|www\.)[^\s<>"')]+)(?![^<]*>)/gi
    let last = 0
    let m: RegExpExecArray | null
    // eslint-disable-next-line no-cond-assign
    while ((m = urlRe.exec(text)) !== null) {
      const [url] = m
      const before = text.slice(last, m.index)
      if (before) parts.push(before)
      const href = url.startsWith('http') ? url : `https://${url}`
      parts.push(
        <Box key={`u-${last}-${m.index}`} component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
          {url}
        </Box>,
      )
      last = m.index + url.length
    }
    const tail = text.slice(last)
    if (tail) parts.push(tail)
    return parts
  }

  let lastIndex = 0
  let match: RegExpExecArray | null
  // eslint-disable-next-line no-cond-assign
  while ((match = anchorRe.exec(desc)) !== null) {
    const [full, href, inner] = match
    const before = desc.slice(lastIndex, match.index)
    if (before) nodes.push(...linkify(stripTags(before)))
    nodes.push(
      <Box key={`a-${match.index}`} component="a" href={href} target="_blank" rel="noopener noreferrer" sx={linkSx}>
        {stripTags(inner)}
      </Box>,
    )
    lastIndex = match.index + full.length
  }
  const rest = desc.slice(lastIndex)
  if (rest) nodes.push(...linkify(stripTags(rest)))

  return nodes
}

/**
 * Shared HIBP breach list — logo, title/date, domain/account-count/verified
 * line, DataClasses chips, and a click-to-expand Description (accordion:
 * opening one closes whichever was open). Self-contained (owns its own
 * show-more and expanded-row state) so both the Breaches card and Provider
 * Coverage's `hbp` panel can drop in the exact same rendering instead of
 * keeping two copies in sync.
 */
export function BreachList({ breaches, cap = 5 }: { breaches: any[]; cap?: number }) {
  const tk = useTokens()
  const { visible, open, setOpen, hiddenCount } = useShowMore(breaches, cap)
  const [openIdx, setOpenIdx] = React.useState<number | null>(null)

  return (
    <>
      {visible.map((b: any, i: number) => {
        const rawDescription = String(b.Description || '')
        const description = rawDescription.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
        const isOpen = openIdx === i
        return (
          <Box key={i} sx={{ pt: i ? '10px' : 0, mt: i ? '10px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
            <Box
              component="button"
              type="button"
              disabled={!description}
              onClick={() => setOpenIdx((cur) => (cur === i ? null : i))}
              sx={{
                display: 'flex', gap: '10px', alignItems: 'flex-start', width: '100%', textAlign: 'left',
                background: 'none', border: 0, padding: '4px', margin: '-4px', borderRadius: '8px',
                fontFamily: 'inherit', color: 'inherit', cursor: description ? 'pointer' : 'default',
                '&:hover': description ? { background: tk.surface3 } : {},
              }}
            >
              <BreachLogo src={b.LogoPath} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: tk.text }}>
                    {b.Title || b.Name}{b.BreachDate ? ` · ${b.BreachDate}` : ''}
                  </Typography>
                  {description && (
                    <Box
                      component="span"
                      sx={{
                        ml: 'auto', flex: 'none', display: 'flex', color: tk.text3,
                        transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s',
                      }}
                    >
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </Box>
                  )}
                </Box>
                <Typography sx={{ fontSize: 10.5, color: tk.text3 }}>
                  {[b.Domain, b.PwnCount ? `${Number(b.PwnCount).toLocaleString()} accounts` : null, b.IsVerified ? 'verified' : null].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
            {Array.isArray(b.DataClasses) && b.DataClasses.length > 0 && (
              <Box sx={{ mt: '6px', pl: '36px' }}><Chips items={b.DataClasses.slice(0, 5)} /></Box>
            )}
            {isOpen && description && (
              <Typography sx={{ fontSize: 11.5, color: tk.text2, lineHeight: 1.5, mt: '8px', pl: '36px' }}>
                {renderBreachDescription(rawDescription)}
              </Typography>
            )}
          </Box>
        )
      })}
      <ShowMoreButton open={open} hiddenCount={hiddenCount} onClick={() => setOpen((o) => !o)} />
    </>
  )
}

/**
 * A link preview card — thumbnail (or a colored initial tile when there's no
 * image, or the image fails to load), title, optional description/date, and
 * a "Source" link out to the original URL.
 *
 * By default this only ever renders what's already in the data — most
 * providers don't return a title/description/image for an arbitrary link,
 * and callers that have nothing but a bare URL just get the fallback tile.
 * Pass `livePreview` when the caller genuinely has nothing else to go on
 * (e.g. PersonTemplate's Websites card, whose `luna.person.urls` are bare
 * links with no stored metadata at all): this fires the same live
 * og:title/og:description/og:image proxy fetch the old UI's "Social
 * Networks:" card used (`GET /searcher/url?url=`, via `useLinkPreview`),
 * scoped to only fire when there's no `image` already on hand.
 */
export function LinkPreviewCard({
  url,
  title,
  image,
  description,
  date,
  livePreview = false,
}: {
  url: string
  title: string
  image?: string
  description?: string
  date?: string
  livePreview?: boolean
}) {
  const tk = useTokens()
  const [broken, setBroken] = React.useState(false)
  const needsPreview = livePreview && !image
  const preview = useLinkPreview(needsPreview ? url : undefined)
  const loading = needsPreview && preview.status === 'loading'
  const fetchedImage = needsPreview ? preview.data?.images?.[0] : undefined
  const fetchedTitle = needsPreview ? preview.data?.title?.replace(/\s+/g, ' ').trim() : undefined
  const fetchedDescription = needsPreview ? preview.data?.description?.replace(/\s+/g, ' ').trim() : undefined
  const resolvedImage = image || fetchedImage
  const resolvedTitle = fetchedTitle || title
  const resolvedDescription = description || fetchedDescription
  const showImage = Boolean(resolvedImage) && !broken
  const letter = (resolvedTitle || url.replace(/^https?:\/\//, '')).trim().charAt(0).toUpperCase() || '?'

  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex', gap: '8px', p: '8px', textDecoration: 'none', color: 'inherit',
        border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, background: tk.surface2,
        width: '100%', minWidth: 0,
        '&:hover': { borderColor: tk.borderStrong },
      }}
    >
      <Box sx={{
        width: 66, height: 66, borderRadius: '9px', flex: 'none', overflow: 'hidden',
        display: 'grid', placeItems: 'center', background: showImage ? tk.surface3 : sourceColor(resolvedTitle || url),
      }}
      >
        {loading ? (
          <CircularProgress size={18} thickness={5} sx={{ color: '#fff' }} />
        ) : showImage ? (
          <Box component="img" src={resolvedImage} alt="" onError={() => setBroken(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>{letter}</Typography>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: tk.text, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {resolvedTitle}
        </Typography>
        {resolvedDescription && (
          <Typography sx={{ fontSize: 11, color: tk.text3, mt: '3px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {resolvedDescription}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '6px' }}>
          {date && <Typography sx={{ fontSize: 10, color: tk.text3 }}>{date}</Typography>}
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: '#10B981' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 17H7a5 5 0 0 1 0-10h2" />
              <path d="M15 7h2a5 5 0 1 1 0 10h-2" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export function Warn({ title, children }: { title: string; children?: React.ReactNode }) {
  const tk = useTokens()
  return (
    <Box
      sx={{
        display: 'flex', alignItems: 'flex-start', gap: '9px', fontSize: 12, color: tk.text2,
        background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.3)', borderRadius: `${radii.md}px`, p: '10px 12px',
      }}
    >
      <Box component="span" sx={{ color: '#F59E0B', flex: 'none', display: 'flex', mt: '1px' }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 9v5M12 17h.01M10.3 3.9 2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        </svg>
      </Box>
      <Box>
        <Typography component="b" sx={{ display: 'block', color: '#F59E0B', mb: '2px', fontWeight: 700, fontSize: 12 }}>{title}</Typography>
        {children}
      </Box>
    </Box>
  )
}

export function Verdict({
  tone,
  title,
  subtitle,
  icon,
  children,
}: {
  tone: 'ok' | 'bad' | 'warn'
  title: string
  subtitle?: string
  icon?: React.ReactNode
  children?: React.ReactNode
}) {
  const tk = useTokens()
  const c = tone === 'ok' ? '#10B981' : tone === 'bad' ? '#EF4444' : '#F59E0B'
  const bg = tone === 'ok' ? 'rgba(16,185,129,.12)' : tone === 'bad' ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.12)'
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
      <Box sx={{ width: 70, height: 70, borderRadius: '20px', display: 'grid', placeItems: 'center', flex: 'none', background: bg, color: c }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 180 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 21, letterSpacing: '-.02em', color: c }}>{sentence(title)}</Typography>
        {subtitle && <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '3px' }}>{subtitle}</Typography>}
      </Box>
      {children}
    </Box>
  )
}

/** Compare two values side by side — used by the validators. */
export function Compare({ left, right, ok }: { left: [string, string, string]; right: [string, string, string]; ok: boolean }) {
  const tk = useTokens()
  const cell = (v: [string, string, string]) => (
    <Box sx={{ background: tk.surface2, border: `1px solid ${tk.border}`, borderRadius: `${radii.md}px`, p: '12px', textAlign: 'center' }}>
      <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.07em', color: tk.text3, fontWeight: 700 }}>{v[0]}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: 17, mt: '3px', color: tk.text }}>{v[1]}</Typography>
      <Typography sx={{ fontSize: 11, color: tk.text3 }}>{v[2]}</Typography>
    </Box>
  )
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '14px', alignItems: 'center', mt: '14px', '@media (max-width:680px)': { gridTemplateColumns: '1fr' } }}>
      {cell(left)}
      <Box sx={{ color: ok ? '#10B981' : '#EF4444', textAlign: 'center' }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </Box>
      {cell(right)}
    </Box>
  )
}

export function RawCard({ data }: { data: unknown }) {
  const [open, setOpen] = React.useState(false)
  const json = React.useMemo(() => {
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return '// payload could not be serialised'
    }
  }, [data])

  return (
    <ResCard
      title="Raw response"
      action={
        <Box component="button" onClick={() => setOpen((o) => !o)} sx={{ border: 0, background: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: '#2D89F4' }}>
          {open ? 'hide' : 'show'}
        </Box>
      }
    >
      {open && (
        <Box
          component="pre"
          sx={{
            background: '#0B081D', color: '#9FE8C0', borderRadius: `${radii.md}px`, p: '13px', m: 0,
            fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, lineHeight: 1.6, overflow: 'auto', maxHeight: 420,
          }}
        >
          {json}
        </Box>
      )}
    </ResCard>
  )
}

/**
 * Empty-state used when a workflow returns nothing at all. This is always
 * the entire root of whichever template renders it (every *Template.tsx's
 * `if (!x) return <NoData .../>` early-return), so it fills the main
 * column's grid cell via ResCard's `fill` — otherwise it sat as a short,
 * fixed-height box with a large gap of empty page below it before the
 * footer, instead of the panel itself stretching down to meet the footer.
 */
export function NoData({ title, hint }: { title: string; hint?: string }) {
  const tk = useTokens()
  const theme = useTheme()
  return (
    <ResCard fill>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: tk.text3 }}>
        <Box
          component="img"
          src={theme.palette.mode === 'dark' ? noResultsDark : noResultsLight}
          alt=""
          sx={{ width: 150, height: 'auto', mb: '18px' }}
        />
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: tk.text2 }}>{title}</Typography>
        {hint && <Typography sx={{ fontSize: 12.5, mt: '5px', color: tk.text3, maxWidth: 360 }}>{hint}</Typography>}
      </Box>
    </ResCard>
  )
}

/**
 * Radar-style "still searching" animation — a rotating sweep wedge plus two
 * outward-pinging rings, all built from plain CSS `@keyframes` on `Box`es
 * (same inline-keyframes-on-sx approach as the in-progress dot in
 * RenderTableBody.tsx's `StatusPill`, which this reuses the amber `#F59E0B`
 * from for visual consistency with the rest of the "in progress" language).
 */
export function RadarSpinner({ size = 76 }: { size?: number | string }) {
  const tk = useTokens()
  const AMBER = '#F59E0B'
  // number stays px (existing callers); a string (e.g. '75vmin') passes through
  // as-is, for the full-page InProgress state below.
  const dim = typeof size === 'number' ? `${size}px` : size
  return (
    <Box sx={{ position: 'relative', width: dim, height: dim, flex: 'none' }}>
      {[0, 0.8].map((delay) => (
        <Box
          key={delay}
          sx={{
            position: 'absolute', inset: 0, borderRadius: '50%',
            border: `1.5px solid ${AMBER}`,
            animation: `radarPing 2.4s ${delay}s infinite ease-out`,
            '@keyframes radarPing': {
              '0%': { transform: 'scale(.35)', opacity: 0.8 },
              '100%': { transform: 'scale(1)', opacity: 0 },
            },
          }}
        />
      ))}
      <Box
        sx={{
          position: 'absolute', inset: 0, borderRadius: '50%', overflow: 'hidden',
          background: `conic-gradient(from 0deg, rgba(245,158,11,0) 0deg, rgba(245,158,11,.6) 55deg, rgba(245,158,11,0) 95deg)`,
          animation: 'radarSweep 1.6s linear infinite',
          '@keyframes radarSweep': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `1px solid ${tk.border}` }} />
      <Box
        sx={{
          // percentage of the radar's own box, not a fixed px — so the
          // centre dot still looks right at 76px and at 75% of the page
          position: 'absolute', top: '50%', left: '50%', width: '10%', height: '10%', borderRadius: '50%',
          background: AMBER, transform: 'translate(-50%,-50%)', boxShadow: `0 0 9px 2px ${AMBER}99`,
        }}
      />
    </Box>
  )
}

/**
 * "Search in progress" state — replaces the workflow template entirely while
 * `row.status === 'progress'` (ResultView.tsx), the same way `NoData` above
 * replaces it for an empty result: the data isn't final yet, so showing a
 * half-populated template would be misleading. `fill`ed like `NoData` for
 * the same reason (a short panel with a big empty gap before the footer).
 */
export function InProgress({ percentage }: { percentage?: string | number }) {
  const tk = useTokens()
  return (
    <ResCard fill>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
        {/*
          50% of the page's own smaller dimension (vmin, not vw/vh alone) so
          the radar stays a circle and never outgrows the viewport on a tall
          narrow window — capped at 560px so it doesn't get absurd on a huge
          monitor.
        */}
        <RadarSpinner size="min(50vmin, 560px)" />
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: tk.text2, mt: '18px' }}>
          Search in progress
        </Typography>
        <Typography sx={{ fontSize: 12.5, mt: '5px', color: tk.text3, maxWidth: 360 }}>
          Results will appear here automatically.
        </Typography>
      </Box>
    </ResCard>
  )
}

/**
 * Small floating "still searching" notice — used instead of `InProgress`
 * once at least one provider has already returned data (ResultView.tsx):
 * the template itself renders that partial data right away rather than
 * being hidden behind a full-panel state, so this only needs to say more is
 * still coming. Bottom-right corner, same visual language as the app's
 * global toast (container/Snackbar.tsx — tinted-wash card, 14px radius,
 * `shadows.raised`) so it reads as "notification", not as a second content
 * panel. `position: fixed` + a high `zIndex` so it floats above the Dialog
 * this result view is usually presented in (MUI's default modal zIndex is
 * 1300 — snackbar's 1400 clears it the same way the global Snackbar does).
 */
export function ProgressToast({ percentage }: { percentage?: string | number }) {
  const theme = useTheme()
  const tk = tokensFor(theme.palette.mode)
  const AMBER = '#F59E0B'
  return (
    <Box
      sx={{
        position: 'fixed', bottom: 22, right: 22, zIndex: theme.zIndex.snackbar,
        display: 'flex', alignItems: 'center', gap: '12px',
        background: `linear-gradient(${AMBER}1F, ${AMBER}1F), ${tk.surface}`,
        border: `1px solid ${AMBER}55`,
        borderRadius: '14px',
        boxShadow: '0 18px 40px -18px rgba(0,0,0,.6)',
        p: '10px 16px 10px 12px',
        minWidth: 240,
        maxWidth: 320,
        pointerEvents: 'none',
      }}
    >
      <RadarSpinner size={32} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 12.5, color: tk.text }}>
          Search in progress
        </Typography>
        <Typography sx={{ fontSize: 11, color: tk.text3, mt: '1px' }}>
          {percentage ? `About ${percentage}% complete` : 'More results may still arrive'}
        </Typography>
      </Box>
    </Box>
  )
}
